import { s as script$1 } from './Icon-fJqkx1gN.js';
import { s as script$2 } from './Input-0ivJhXUC.js';
import { C as CompatFallthroughMixin } from './CompatFallthroughMixin-Ev2XGfi3.js';
import { F as FormElementMixin } from './FormElementMixin-ILDlgM3i.js';
import { resolveComponent, openBlock, createElementBlock, mergeProps, Fragment, renderList, normalizeClass, createElementVNode, withModifiers, createVNode } from 'vue';
import { a as registerComponent } from './plugins-C7548x21.js';
import './config-nHWiBviw.js';
import './helpers-O9h6_ICO.js';
import './typeof-Z4rCTFCZ.js';

var script = {
    name: 'BNumberinput',
    components: {
        [script$1.name]: script$1,
        [script$2.name]: script$2
    },
    mixins: [CompatFallthroughMixin, FormElementMixin],
    inject: {
        field: {
            from: 'BField',
            default: false
        }
    },
    props: {
        modelValue: Number,
        min: {
            type: [Number, String]
        },
        max: [Number, String],
        step: [Number, String],
        minStep: [Number, String],
        exponential: [Boolean, Number],
        disabled: Boolean,
        type: {
            type: String,
            default: 'is-primary'
        },
        editable: {
            type: Boolean,
            default: true
        },
        controls: {
            type: Boolean,
            default: true
        },
        controlsAlignment: {
            type: String,
            default: 'center',
            validator: (value) => {
                return [
                    'left',
                    'right',
                    'center'
                ].indexOf(value) >= 0
            }
        },
        controlsRounded: {
            type: Boolean,
            default: false
        },
        controlsPosition: String,
        placeholder: [Number, String],
        ariaMinusLabel: String,
        ariaPlusLabel: String,
        longPress: {
            type: Boolean,
            default: true
        }
    },
    emits: ['blur', 'focus', 'update:modelValue'],
    data() {
        return {
            newValue: this.modelValue,
            newStep: this.step || 1,
            newMinStep: this.minStep,
            timesPressed: 1,
            _elementRef: 'input'
        }
    },
    computed: {
        computedValue: {
            get() {
                return this.newValue
            },
            set(value) {
                // Parses the number, so that "0" => 0, and "invalid" => null
                let newValue = (Number(value) === 0) ? 0 : (Number(value) || null);
                if (value === '' || value === undefined || value === null) {
                    newValue = null;
                }
                this.newValue = newValue;
                if (newValue === null) {
                    this.$emit('update:modelValue', newValue);
                } else if (!isNaN(newValue) && newValue !== '-0') {
                    this.$emit('update:modelValue', Number(newValue));
                }
                this.$nextTick(() => {
                    if (this.$refs.input) {
                        this.$refs.input.checkHtml5Validity();
                    }
                });
            }
        },
        controlsLeft() {
            if (this.controls && this.controlsAlignment !== 'right') {
                return this.controlsAlignment === 'left' ? ['minus', 'plus'] : ['minus']
            }
            return []
        },
        controlsRight() {
            if (this.controls && this.controlsAlignment !== 'left') {
                return this.controlsAlignment === 'right' ? ['minus', 'plus'] : ['plus']
            }
            return []
        },
        fieldClasses() {
            return [
                { 'has-addons': this.controlsPosition === 'compact' },
                { 'is-grouped': this.controlsPosition !== 'compact' },
                { 'is-expanded': this.expanded }
            ]
        },
        buttonClasses() {
            return [this.type, this.size, { 'is-rounded': this.controlsRounded }]
        },
        minNumber() {
            return typeof this.min === 'string' ? parseFloat(this.min) : this.min
        },
        maxNumber() {
            return typeof this.max === 'string' ? parseFloat(this.max) : this.max
        },
        stepNumber() {
            if (this.newStep === 'any') {
                return 1
            }
            return typeof this.newStep === 'string' ? parseFloat(this.newStep) : this.newStep
        },
        minStepNumber() {
            if (this.newStep === 'any' && typeof this.newMinStep === 'undefined') {
                return 'any'
            }
            const step = typeof this.newMinStep !== 'undefined' ? this.newMinStep : this.newStep;
            return typeof step === 'string' ? parseFloat(step) : step
        },
        disabledMin() {
            return this.computedValue - this.stepNumber < this.minNumber
        },
        disabledMax() {
            return this.computedValue + this.stepNumber > this.maxNumber
        },
        stepDecimals() {
            const step = this.minStepNumber.toString();
            const index = step.indexOf('.');
            if (index >= 0) {
                return step.substring(index + 1).length
            }
            return 0
        },

        disabledOrUndefined() {
            // On Vue 3, setting a boolean attribute `false` does not remove it,
            // `null` or `undefined` has to be given to remove it.
            return this.disabled || undefined
        }
    },
    watch: {
    /**
     * When v-model is changed:
     *   1. Set internal value.
     */
        modelValue: {
            immediate: true,
            handler(value) {
                this.newValue = value;
            }
        },
        step(value) {
            this.newStep = value;
        },
        minStep(value) {
            this.newMinStep = value;
        }
    },
    methods: {
        isDisabled(control) {
            return this.disabled || (control === 'plus' ? this.disabledMax : this.disabledMin)
        },
        decrement() {
            if (this.computedValue === null || typeof this.computedValue === 'undefined') {
                if (this.maxNumber !== null && typeof this.maxNumber !== 'undefined') {
                    this.computedValue = this.maxNumber;
                    return
                }
                this.computedValue = 0;
            }
            if (typeof this.minNumber === 'undefined' || (this.computedValue - this.stepNumber) >= this.minNumber) {
                const value = this.computedValue - this.stepNumber;
                this.computedValue = parseFloat(value.toFixed(this.stepDecimals));
            }
        },
        increment() {
            if (this.computedValue === null || typeof this.computedValue === 'undefined' || this.computedValue < this.minNumber) {
                if (this.minNumber !== null && typeof this.minNumber !== 'undefined') {
                    this.computedValue = this.minNumber;
                    return
                }
                this.computedValue = 0;
            }
            if (typeof this.maxNumber === 'undefined' || (this.computedValue + this.stepNumber) <= this.maxNumber) {
                const value = this.computedValue + this.stepNumber;
                this.computedValue = parseFloat(value.toFixed(this.stepDecimals));
            }
        },
        onControlClick(event, inc) {
            // IE 11 -> filter click event
            if (event.detail !== 0 || event.type !== 'click') return
            if (inc) this.increment();
            else this.decrement();
        },
        longPressTick(inc) {
            if (inc) this.increment();
            else this.decrement();

            if (!this.longPress) return
            this._$intervalRef = setTimeout(() => {
                this.longPressTick(inc);
            }, this.exponential ? (250 / (this.exponential * this.timesPressed++)) : 250);
        },
        onStartLongPress(event, inc) {
            if (event.button !== 0 && event.type !== 'touchstart') return
            clearTimeout(this._$intervalRef);
            this.longPressTick(inc);
        },
        onStopLongPress() {
            if (!this._$intervalRef) return
            this.timesPressed = 1;
            clearTimeout(this._$intervalRef);
            this._$intervalRef = null;
        }
    },
    mounted() {
        // tells the field that it is wrapping a number input
        // if the field is the direct parent.
        if (this.field === this.$parent) {
            this.$parent.wrapNumberinput({
                controlsPosition: this.controlsPosition,
                size: this.size
            });
        }
    },

    beforeUnmount() {
        clearTimeout(this._$intervalRef);
    }
};

const _hoisted_1 = ["disabled", "aria-label", "onMousedown", "onTouchstart", "onClick"];
const _hoisted_2 = ["disabled", "aria-label", "onMousedown", "onTouchstart", "onClick"];

function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_b_icon = resolveComponent("b-icon");
  const _component_b_input = resolveComponent("b-input");

  return (openBlock(), createElementBlock("div", mergeProps({
    class: ["b-numberinput field", $options.fieldClasses]
  }, _ctx.rootAttrs), [
    (openBlock(true), createElementBlock(Fragment, null, renderList($options.controlsLeft, (control) => {
      return (openBlock(), createElementBlock("p", {
        key: control,
        class: normalizeClass(['control', control]),
        onMouseup: _cache[0] || (_cache[0] = (...args) => ($options.onStopLongPress && $options.onStopLongPress(...args))),
        onMouseleave: _cache[1] || (_cache[1] = (...args) => ($options.onStopLongPress && $options.onStopLongPress(...args))),
        onTouchend: _cache[2] || (_cache[2] = (...args) => ($options.onStopLongPress && $options.onStopLongPress(...args))),
        onTouchcancel: _cache[3] || (_cache[3] = (...args) => ($options.onStopLongPress && $options.onStopLongPress(...args)))
      }, [
        createElementVNode("button", {
          type: "button",
          class: normalizeClass(["button", $options.buttonClasses]),
          disabled: $options.isDisabled(control) || undefined,
          "aria-label": control === 'plus' ? $props.ariaPlusLabel : $props.ariaMinusLabel,
          onMousedown: $event => (
                    !$options.isDisabled(control) && $options.onStartLongPress($event, control === 'plus')
                ),
          onTouchstart: withModifiers($event => (
                    !$options.isDisabled(control) && $options.onStartLongPress($event, control === 'plus')
                ), ["prevent"]),
          onClick: $event => (
                    !$options.isDisabled(control) && $options.onControlClick($event, control === 'plus')
                )
        }, [
          createVNode(_component_b_icon, {
            both: "",
            icon: control,
            pack: _ctx.iconPack,
            size: _ctx.iconSize
          }, null, 8 /* PROPS */, ["icon", "pack", "size"])
        ], 42 /* CLASS, PROPS, HYDRATE_EVENTS */, _hoisted_1)
      ], 34 /* CLASS, HYDRATE_EVENTS */))
    }), 128 /* KEYED_FRAGMENT */)),
    createVNode(_component_b_input, mergeProps({
      type: "number",
      ref: "input",
      modelValue: $options.computedValue,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = $event => (($options.computedValue) = $event))
    }, _ctx.fallthroughAttrs, {
      step: $options.minStepNumber,
      max: $props.max,
      min: $props.min,
      size: _ctx.size,
      disabled: $options.disabledOrUndefined,
      readonly: !$props.editable,
      loading: _ctx.loading,
      rounded: _ctx.rounded,
      icon: _ctx.icon,
      "icon-pack": _ctx.iconPack,
      autocomplete: _ctx.autocomplete,
      expanded: _ctx.expanded,
      placeholder: $props.placeholder,
      "use-html5-validation": _ctx.useHtml5Validation,
      onFocus: _cache[5] || (_cache[5] = $event => (_ctx.$emit('focus', $event))),
      onBlur: _cache[6] || (_cache[6] = $event => (_ctx.$emit('blur', $event)))
    }), null, 16 /* FULL_PROPS */, ["modelValue", "step", "max", "min", "size", "disabled", "readonly", "loading", "rounded", "icon", "icon-pack", "autocomplete", "expanded", "placeholder", "use-html5-validation"]),
    (openBlock(true), createElementBlock(Fragment, null, renderList($options.controlsRight, (control) => {
      return (openBlock(), createElementBlock("p", {
        key: control,
        class: normalizeClass(['control', control]),
        onMouseup: _cache[7] || (_cache[7] = (...args) => ($options.onStopLongPress && $options.onStopLongPress(...args))),
        onMouseleave: _cache[8] || (_cache[8] = (...args) => ($options.onStopLongPress && $options.onStopLongPress(...args))),
        onTouchend: _cache[9] || (_cache[9] = (...args) => ($options.onStopLongPress && $options.onStopLongPress(...args))),
        onTouchcancel: _cache[10] || (_cache[10] = (...args) => ($options.onStopLongPress && $options.onStopLongPress(...args)))
      }, [
        createElementVNode("button", {
          type: "button",
          class: normalizeClass(["button", $options.buttonClasses]),
          disabled: $options.isDisabled(control) || undefined,
          "aria-label": control === 'plus' ? $props.ariaPlusLabel : $props.ariaMinusLabel,
          onMousedown: $event => (
                    !$options.isDisabled(control) && $options.onStartLongPress($event, control === 'plus')
                ),
          onTouchstart: withModifiers($event => (
                    !$options.isDisabled(control) && $options.onStartLongPress($event, control === 'plus')
                ), ["prevent"]),
          onClick: $event => (
                    !$options.isDisabled(control) && $options.onControlClick($event, control === 'plus')
                )
        }, [
          createVNode(_component_b_icon, {
            both: "",
            icon: control,
            pack: _ctx.iconPack,
            size: _ctx.iconSize
          }, null, 8 /* PROPS */, ["icon", "pack", "size"])
        ], 42 /* CLASS, PROPS, HYDRATE_EVENTS */, _hoisted_2)
      ], 34 /* CLASS, HYDRATE_EVENTS */))
    }), 128 /* KEYED_FRAGMENT */))
  ], 16 /* FULL_PROPS */))
}

script.render = render;
script.__file = "src/components/numberinput/Numberinput.vue";

var Plugin = {
  install: function install(Vue) {
    registerComponent(Vue, script);
  }
};

export { script as BNumberinput, Plugin as default };
